<script lang="ts">
import AlertDialog from '@/app/common/components/AlertDialog.vue';
import AttachUploader from '@/app/common/components/AttachUploader.vue';
import { formateDate } from '@/app/common/dateFormate';
import { AttachmentUploaderType, AttachmentUrl } from '@/app/common/types/attachUploader';
import { ATTACH_TYPE, ATTACHMENT_OPTIONS, FILE_COUNT_LIMIT, LANG_EN } from '@/app/const';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { ComplaintAdditionalDoc } from '@/interface/Complaint.interface';
import { complaintService, fileService } from '@/services/serviceProvider';
import { ref } from 'vue';

export default {
    components: {
        AttachUploader,
        AlertDialog
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            default: 't-upload-doc',
        },
        cancelText: {
            type: String,
            default: 't-back'
        },
        confirmText: {
            type: String,
            default: 't-confirm'
        },
        additionalDoc: {
            type: Object,
            default: {}
        },
        otherFileNames: {
            type: Array<String>,
            default: []
        }
    },
    emits: [
        'update:modelValue',
        'update:additionalDoc',
        'onConfirm',
        'onCancel'
    ],
    setup() {
        const cancelReqForm = ref();
        const fileSv = fileService;
        return { cancelReqForm, fileSv };
    },
    data() {
        return {
            model: undefined as ComplaintAdditionalDoc | undefined,
            attachUploads: undefined as AttachmentUploaderType[] | undefined,
            attachments: undefined as AttachmentUrl[] | undefined,
            attachmentOptions: ATTACHMENT_OPTIONS,
            failAlert: { display: false } as BaseAlert,
            fileCountLimit: FILE_COUNT_LIMIT,
            en: LANG_EN,
            lang: this.$i18n.locale,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            }
        }
    },
    computed: {
        dialogShow: {
            get() {
                this.model = { ...this.additionalDoc } as ComplaintAdditionalDoc;
                this.attachUploads = this.model.fileUploads ? [...this.model.fileUploads] : [];
                this.attachments = this.model.attachments ? [...this.model.attachments] : []
                return this.modelValue || false
            },
            set(newValue: boolean) {
                this.$emit('update:modelValue', newValue);
            }
        },
        headers() {
            return [
                { title: this.$t('t-order'), sortable: false, key: 'index', align: 'center', width: '5%' },
                { title: this.$t('t-file-name'), key: 'fileName' },
                { title: '', key: 'actions', sortable: false, width: '2%' },
            ] as any[];
        },
    },
    methods: {
        async confirm() {
            const valid = await this.cancelReqForm.validate();
            if (valid.valid) {
                this.dialogShow = false;
                if (this.model) {
                    this.model.attachments = this.attachments;
                    this.model.fileUploads = this.attachUploads;
                }
                this.$emit('onConfirm', this.model);
            }
        },
        cancel() {
            this.dialogShow = false;
            this.$emit('onCancel')
        },
        allAttachments(): any[] {
            let newUploads = this.attachUploads as any[] ?? [];
            newUploads.forEach(up => { up.type = ATTACH_TYPE.NEW });
            let alreadySaved = this.attachments as any[] ?? [];
            alreadySaved.forEach(saved => { saved.type = ATTACH_TYPE.SAVED });
            return newUploads.concat(alreadySaved);
        },
        deleteAttachment(attach: any) {
            let allAttachs = this.allAttachments();
            if (allAttachs && allAttachs.length > 0) {
                if (attach.type === ATTACH_TYPE.NEW) {
                    let newUploads = this.attachUploads;
                    this.attachUploads = newUploads?.filter(o => o.name !== attach.name);
                } else if (attach.type === ATTACH_TYPE.SAVED) {
                    let saved = this.attachments;
                    this.attachments = saved?.filter(o => o.fileName !== attach.fileName);
                }
            }
        },
        dateString(date: Date | undefined) {
            return complaintService.dateString(date);
        },
        documentName(id: number | undefined, remark: string | number | undefined = undefined) {
            if (id && id < 3) {
                const label = this.attachmentOptions.find(o => o.value === id)?.label;
                return this.$t(`t-${label}`)
            }
            else {
                return remark;
            }
        },
        checkDuplicateFileName() {
            let fileNameList = [] as string[];
            let allAttach = this.allAttachments();
            let allFilesUser = allAttach.map(o => {
                if (o.type === ATTACH_TYPE.NEW) {
                    return o.name;
                } else if (o.type === ATTACH_TYPE.SAVED) {
                    return o.fileName;
                }
            })
            allFilesUser.concat(this.otherFileNames).forEach(fileName => {
                if (fileNameList.includes(fileName)) {
                    this.failAlert = {
                        display: true,
                        header: ``,
                        messageTitle: this.$t('t-cannot-duplicate-file-name'),
                        messageText: this.$t('t-please-change-file-name'),
                    } as BaseAlert;
                    let index = this.attachUploads?.findIndex(o => o.name === fileName) as number;
                    if (index >= 0) {
                        this.attachUploads?.splice(index, 1);
                    }
                    return;
                } else {
                    fileNameList.push(fileName);
                }
            })
        },
        overSizeLimit() {
            this.failAlert = {
                display: true,
                messageTitle: this.$t('t-cannot-upload-over-size-limit'),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert;
        },
        errorEmptyFile() {
            this.failAlert = {
                display: true,
                messageTitle: this.$t("t-attachment-error-0byte"),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert;
        },
    }
}
</script>

<template>
    <v-dialog v-model="dialogShow" width="80%" :close-on-back="false" persistent>
        <v-form ref="cancelReqForm" class="admin-form app-form-input" validate-on="submit lazy"
            @submit.prevent="confirm">
            <v-card class="dialog-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t(header) }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn icon="mdi-close" @click="dialogShow = false"></v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-divider class="mx-4"></v-divider>
                <v-card-text>
                    <v-card class="inner-card">
                        <v-card-text>
                            <div class="d-flex justify-space-between">
                                <div class="d-flex align-center">
                                    <div class="text-label mb-0 mr-2">{{ $t('t-complaint-additional-docs') }}</div>
                                    <div>{{ documentName(model?.docTypeNo, model?.remark) }}</div>
                                </div>
                                <div class="d-flex align-center">
                                    <div class="text-label mb-0 mr-2">{{ $t('t-complaint-request-date') }}</div>
                                    <div>{{ dateString(model?.requestDate) }}</div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                    <v-row class="mb-4">
                        <v-col cols="12" class="pb-0">
                            <div class="text-label mb-0">{{ $t('t-upload-doc') }}</div>
                        </v-col>
                        <v-col cols="12" style="min-height: 130px;">
                            <AttachUploader v-model="attachUploads" fileType="attach" :border-class="'radius-lg'"
                                :disabled="allAttachments().length >= fileCountLimit"
                                @on-upload-success="checkDuplicateFileName" @on-over-size-limit="overSizeLimit"
                                @on-error-empty-file="errorEmptyFile">
                            </AttachUploader>
                        </v-col>
                        <v-col cols="12">
                            <v-alert v-if="allAttachments() && allAttachments().length > 0" border="start"
                                border-color="primary" :title="$t('t-upload-file-limit')" density="compact"
                                variant="tonal" color="primary">
                                <template v-slot:text>
                                    <div class="d-flex flex-column">
                                        <span>{{ $t('t-attachment-message') }}</span>
                                        <span v-if="lang === en">{{ $t('t-attachment-message-sub') }}</span>
                                    </div>
                                </template>
                            </v-alert>
                        </v-col>
                    </v-row>
                    <v-data-table :headers="headers" :items="allAttachments()" hide-default-footer
                        :no-data-text="$t('t-no-data-available')" :items-per-page="-1">
                        <template #[`item.index`]="{ index }">
                            <div class="w-100 text-center">{{ index + 1 }}</div>
                        </template>
                        <template #[`item.fileName`]="{ item }">
                            <span v-if="fileSv.isNewAttachment(item.type)">{{ item.name }}</span>
                            <span v-if="fileSv.isSavedAttachment(item.type)">{{ item.fileName }}</span>
                        </template>
                        <template v-slot:item.actions="{ item, index }">
                            <div class="actions d-flex align-center">
                                <v-btn variant="text" color="primary" @click="fileSv.viewFileInEditMode(item.url)">
                                    <v-icon icon="mdi mdi-eye-outline" class="mr-1"></v-icon>
                                    <span>{{ $t('t-view-doc') }}</span>
                                </v-btn>
                                <v-btn variant="text" color="error" @click="deleteAttachment(item)">
                                    <v-icon icon="mdi mdi-close" class="mr-1"></v-icon>
                                    <span>{{ $t('t-delete') }}</span>
                                </v-btn>
                            </div>
                        </template>
                    </v-data-table>
                </v-card-text>
                <v-card-actions>
                    <v-container class="mx-0">
                        <div class="d-flex justify-end">
                            <v-btn @click="cancel" variant="outlined" color="primary" size="large">
                                {{ $t(cancelText) }}
                            </v-btn>
                            <v-btn @click="confirm" variant="flat" color="primary" size="large">
                                {{ $t(confirmText) }}
                            </v-btn>
                        </div>
                    </v-container>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
    <!-- Add/Edit Fail Dialog -->
    <AlertDialog v-model="failAlert.display" :success="false" :title="failAlert.messageTitle"></AlertDialog>
</template>